"use client";

import type { ProtocolChartData } from "@/types";
import React, { useMemo } from "react";
import format from "date-fns/format";
import isSameYear from "date-fns/isSameYear";

import { utcDay } from "d3-time";
import LineChart from "@/components/Ui/LineChart";
import LoaderInPage from "@/components/Loader/InPage";
import useProtocolStats from "@/hooks/api/useProtocolStats";

import { config } from "./config";

interface ProductChartProps {
  name?: string;
  protocolId?: string;
}

export default function ProductChart({ name, protocolId }: ProductChartProps) {
  const { data: protocol } = useProtocolStats(protocolId);

  const chartData: ProtocolChartData[] = useMemo(() => {
    if (!protocol || !Array.isArray(protocol.historical_data.timestamp)) {
      return [];
    }

    return Array(protocol.historical_data.timestamp.length)
      .fill(null)
      .map((_, i) => {
        const date = new Date(protocol.historical_data.timestamp ? protocol.historical_data.timestamp[i] * 1000 : "");
        const formattedDate = utcDay.floor(date);

        return {
          // depositors: protocol.historical_data.depositors[i] ?? 0,
          tvl: protocol.historical_data.tvl ? protocol.historical_data.tvl[i] ?? 0 : 0,
          name: format(date, "MMM d"),
          // vaults: protocol.historical_data.vaults[i] ?? 0,
          date: formattedDate,
          timestamp: formattedDate.getTime(),
        };
      });
  }, [protocol]);

  const { startDate, endDate } = useMemo(
    () => ({
      startDate: chartData[0]?.date,
      endDate: chartData[chartData.length - 1]?.date,
    }),
    [chartData]
  );

  const isLoading = !protocolId;

  if (!chartData.length) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex h-[419px] items-center justify-center rounded-bl-[48px] rounded-br-[48px] rounded-tl-[48px] bg-neutral">
        <LoaderInPage />
      </div>
    );
  }

  return (
    <div className="custom-scrollbar overflow-x-auto rounded-bl-[48px] rounded-br-[48px] rounded-tl-[48px] bg-neutral pb-[20px] pt-[25px] md:overflow-hidden">
      <h2 className="pl-[45px] font-mono text-[16px] font-bold uppercase">
        {Boolean(name) && `${name} `}
        <span className="font-normal">
          {format(startDate, isSameYear(startDate, endDate) ? "MMM d" : "MMM d yyyy")} - {format(endDate, "MMM d yyyy")}
        </span>
      </h2>

      <div id="chartContainer" className="h-[350px] min-w-[600px]">
        {/* @ts-ignore */}
        <LineChart data={chartData} config={config} />
      </div>
    </div>
  );
}
